import {
    collection,
    getDocs,
    getDoc,
    doc,
    query,
    where,
    orderBy,
    deleteDoc,
    updateDoc,
  } from "firebase/firestore";
  import { db } from "@/firebase/firebase";
  
  export interface BlogPost {
    id: string;
    title: string;
    slug: string;
    description: string;
    content: string;
    author: string;
    datePublished: string; // ISO string
    keywords?: string[];
    state?: string;
    image?: string;
    category?: string;
    date?: string;
  }
  
  // datePublished may be a Firestore Timestamp OR an ISO string. Normalize both
  // to an ISO string without ever throwing — calling .toDate() on a string
  // throws and makes the post render "not found".
  function toISODate(v: unknown): string {
    if (!v) return "";
    if (typeof v === "string") return v;
    if (
      typeof v === "object" &&
      v !== null &&
      "toDate" in v &&
      typeof (v as { toDate: unknown }).toDate === "function"
    ) {
      try {
        return (v as { toDate: () => Date }).toDate().toISOString();
      } catch {
        return "";
      }
    }
    return "";
  }

  // Get all blog posts (sorted newest first)
  export const getBlogPosts = async (): Promise<BlogPost[]> => {
    const q = query(
      collection(db, "blogPosts"),
      orderBy("datePublished", "desc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        title: data.title,
        slug: data.slug,
        description: data.description,
        content: data.content,
        author: data.author,
        image: data.image,
        keywords: data.keywords || [],
        state: data.state || "",
        datePublished: toISODate(data.datePublished),
        category: data.category || "",
        date: data.date || "",
      };
    });
  };
  
  // Get single blog post by slug
  export const getBlogPostBySlug = async (
    slug: string
  ): Promise<BlogPost | null> => {
    const q = query(
      collection(db, "blogPosts"),
      where("slug", "==", slug)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const data = snapshot.docs[0].data();
    return {
      id: snapshot.docs[0].id,
      title: data.title,
      slug: data.slug,
      description: data.description,
      content: data.content,
      author: data.author,
      image: data.image,
      keywords: data.keywords || [],
      state: data.state || "",
      datePublished: toISODate(data.datePublished),
      category: data.category || "",
      date: data.date || "",
    };
  };
  
  // Get single blog post by Firestore ID
  export const getBlogPostById = async (
    id: string
  ): Promise<BlogPost | null> => {
    const docRef = doc(db, "blogPosts", id);
    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) return null;
    const data = snapshot.data();
    return {
      id: snapshot.id,
      title: data.title,
      slug: data.slug,
      description: data.description,
      content: data.content,
      author: data.author,
      image: data.image,
      keywords: data.keywords || [],
      state: data.state || "",
      datePublished: toISODate(data.datePublished),
      category: data.category || "",
      date: data.date || "",
    };
  };
  
  // Delete a blog post by ID
  export const deleteBlogPost = async (
    id: string
  ): Promise<void> => {
    await deleteDoc(doc(db, "blogPosts", id));
  };
  
  // Update a blog post by ID
  export const updateBlogPost = async (
    id: string,
    updatedData: Partial<Omit<BlogPost, 'id'>>
  ): Promise<void> => {
    const ref = doc(db, "blogPosts", id);
    const dataToUpdate: any = { ...updatedData };
    if (updatedData.datePublished) {
      dataToUpdate.datePublished = new Date(
        updatedData.datePublished
      );
    }
    await updateDoc(ref, dataToUpdate);
  };
  